'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, MapPin, Calendar, Users } from 'lucide-react';

const CITY_TO_HOTEL: Record<string, string> = {
  'Algiers': 'prop-1',
  'Oran': 'prop-2',
  'Constantine': 'prop-3',
  'Tamanrasset': 'prop-4',
  'Tlemcen': 'prop-3',
  'Annaba': 'prop-2',
};

const DESTINATION_SUGGESTIONS = [
  'Algiers', 'Oran', 'Constantine', 'Tamanrasset', 'Tlemcen', 'Annaba',
];

export default function HeroSection() {
  const router = useRouter();
  const [destination, setDestination] = useState('');

  const handleSearch = () => {
    const trimmed = destination.trim();
    if (!trimmed) {
      router.push('/hotels');
      return;
    }
    router.push(`/hotels?city=${encodeURIComponent(trimmed)}`);
  };

  const handleCityTag = (city: string) => {
    setDestination(city);
    router.push(`/hotels?city=${encodeURIComponent(city)}`);
  };

  return (
    <section className="relative overflow-hidden min-h-[520px] sm:min-h-[580px] flex items-center">
      {/* Background layers */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1600&q=80)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, rgba(200,61,18,0.72) 0%, rgba(27,108,168,0.55) 60%, rgba(0,0,0,0.45) 100%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8 sm:py-20">
        {/* Headline */}
        <div className="max-w-2xl mb-5 sm:mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-xs font-display font-600 tracking-wide mb-3 sm:mb-4 backdrop-blur-sm border border-white/25">
            🇩🇿 Algeria's #1 Travel Platform
          </span>
          <h1 className="font-display text-2xl sm:text-5xl lg:text-6xl font-800 text-white leading-tight mb-2 sm:mb-4"
            style={{ fontWeight: 800, textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Discover the Soul<br />
            <span style={{
              background: 'linear-gradient(90deg, #F4845F, #FDE0D3)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>of Algeria</span>
          </h1>
          <p className="text-white/85 text-xs sm:text-lg font-body leading-relaxed max-w-lg">
            From the Sahara dunes to Mediterranean shores — find and book the perfect stay across Algeria's most stunning destinations.
          </p>
        </div>

        {/* Search Card */}
        <div className="glass rounded-2xl p-3 sm:p-5 max-w-3xl shadow-card-lg border border-white/30">
          <div className="flex flex-col gap-2 sm:gap-3">
            {/* Destination */}
            <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 sm:py-3.5 border border-gray-100">
              <MapPin size={18} className="text-primary-500 shrink-0" />
              <input
                type="text"
                placeholder="Where in Algeria?"
                value={destination}
                onChange={(e) => setDestination(e?.target?.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 text-sm font-body text-gray-800 placeholder-gray-400 bg-transparent outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {/* Check-in */}
              <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-3 sm:py-3.5 border border-gray-100">
                <Calendar size={16} className="text-primary-500 shrink-0" />
                <input
                  type="text"
                  placeholder="Check-in"
                  className="flex-1 text-sm font-body text-gray-800 placeholder-gray-400 bg-transparent outline-none w-full min-w-0"
                  readOnly
                />
              </div>

              {/* Guests */}
              <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-3 sm:py-3.5 border border-gray-100">
                <Users size={16} className="text-primary-500 shrink-0" />
                <span className="text-sm font-body text-gray-400 truncate">2 guests</span>
              </div>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5 rounded-xl bg-primary-500 text-white text-sm font-display font-600 hover:bg-primary-600 active:scale-95 transition-all duration-150 shadow-primary"
              style={{ fontWeight: 600 }}
            >
              <Search size={16} />
              Search Hotels
            </button>
          </div>

          {/* Quick tags */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3">
            {DESTINATION_SUGGESTIONS.map((city) => (
              <button
                key={city}
                onClick={() => handleCityTag(city)}
                className="px-2.5 py-1 rounded-full text-xs font-body text-white/90 bg-white/15 hover:bg-white/30 border border-white/20 transition-all duration-150 backdrop-blur-sm active:scale-95"
              >
                {city}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
